'use client';

import { cn } from '@core/utils';
import type { ReactNode } from 'react';

import { AdBadge } from '@/app/_components/ads';
import type { AdUnitId } from '@/constants/ad';
import { AD_MOCK_DATA, AD_MOCK_MODE, isAdHidden, useAd } from '@/hooks/useAd';

interface BenefitAdCardProps {
  unitId: AdUnitId;
  backgroundColor?: string;
  className?: string;
  fallback?: ReactNode;
}

export const BenefitAdCard = ({
  unitId,
  backgroundColor = '#F0FDF4',
  className,
  fallback,
}: BenefitAdCardProps) => {
  const { containerRef, adState } = useAd(unitId);

  if (isAdHidden(adState)) {
    return fallback ?? null;
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'group relative block min-h-140 cursor-pointer rounded-large p-20 shadow-sm active:scale-[0.99]',
        adState === 'loading' && 'animate-pulse',
        adState === 'idle' && 'opacity-0',
        className,
      )}
      style={{ backgroundColor }}
    >
      <AdBadge />
      <div className="flex flex-row mobile-sm:flex-col">
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <p
            data-adrop-native="headline"
            className="whitespace-pre-line font-600 text-Gray-600 typo-caption1"
          >
            {AD_MOCK_MODE && AD_MOCK_DATA.headline}
          </p>
          <p
            data-adrop-native="body"
            className="whitespace-pre-line font-700 leading-6 text-Gray-900 typo-body3 group-hover:text-Gray-800"
          >
            {AD_MOCK_MODE && AD_MOCK_DATA.body}
          </p>
        </div>
        <div className="flex shrink-0 items-end justify-end">
          <div
            className={cn(
              'size-60 overflow-hidden rounded-small',
              adState === 'loading' && 'bg-Gray-100',
            )}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              data-adrop-native="asset"
              src={AD_MOCK_MODE ? AD_MOCK_DATA.assetSquare : undefined}
              alt=""
              className="size-full rounded-small border-0 object-contain transition-transform duration-200 ease-out group-hover:scale-110"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
